import React, { useEffect, useState, useCallback } from 'react';
import { Card, Typography, Button, Skeleton, Alert, Modal, Input, Form, message, Row, Col } from 'antd';
import {
    ShoppingCartOutlined,
    EditOutlined,
    PlusOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
    deleteProduct,
    fetchAllProducts,
    editProduct,
    createProduct,
} from '../../api/merchandise';
import { searchContestants } from '../../api/contestant';
import { loadState } from '../../stores/store';
import { useSettings } from '../../contexts/settingsContext';
import { getInvoicesByContestant, addMerchToInvoice } from '../../api/invoice'; // Ensure this import path is correct  
import './merchandise.scss';

const { Title, Text } = Typography;
const { confirm } = Modal;

const MerchandiseItem = React.memo(({ item, role, handleDelete, handleAddToInvoice, showModal }) => {
    return (
        <Col key={item._id}>
            <Card bordered={false} className="main-content">
                {role === 'admin' && (
                    <CloseCircleOutlined
                        className="delete-icon"
                        onClick={() => handleDelete(item._id)}
                    />
                )}
                <div className="content">
                    <img src={item.image} alt={item.description} className="product-image" />
                    <Text className="description">{item.description}</Text>
                    <Text className="price">R{item.price}</Text>
                    {role === 'admin' ? (
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            className="edit-product"
                            onClick={() => showModal(item)}
                        >
                            Edit
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            icon={<ShoppingCartOutlined />}
                            className="add-to-cart"
                            onClick={() => handleAddToInvoice(item)}
                        >
                            Add to Invoice
                        </Button>
                    )}
                </div>
            </Card>
        </Col>
    );
});

const Merchandise = () => {
    const [merchandiseItems, setMerchandiseItems] = useState([]);
    const [contestant, setContestant] = useState(null);
    const [invoice, setInvoice] = useState([]); // State to hold invoices  
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();
    const auth = loadState();
    const { state: settings } = useSettings();

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const data = await fetchAllProducts();
                setMerchandiseItems(data);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchContestantAndInvoices = async () => {
            try {
                const data = await searchContestants();
                setContestant(data[0]);
                if (data[0]) {
                    const invoices = await getInvoicesByContestant(
                        data[0]._id,
                        settings.selectedRound
                    );
                    setInvoice(invoices[0]);
                }
            } catch (err) {
                setError(err.message);
            }
        };

        fetchProducts();
        if (auth.user.role === 'contestant') {
            fetchContestantAndInvoices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps  
    }, [auth.user.role, settings.selectedRound]); // Update dependencies  

    const showModal = useCallback((product) => {
        setCurrentProduct(product);
        setIsModalVisible(true);
    }, []);

    const showCreateModal = useCallback(() => {
        setIsCreateModalVisible(true);
    }, []);

    const handleOk = useCallback(
        async (values) => {
            try {
                const updatedProduct = await editProduct(
                    currentProduct._id,
                    values.image,
                    values.description,
                    values.price
                );
                setMerchandiseItems((prevItems) =>
                    prevItems.map((item) =>
                        item._id === updatedProduct._id ? updatedProduct : item
                    )
                );
                setIsModalVisible(false);
            } catch (err) {
                setError(err.message);
            }
        },
        [currentProduct]
    );

    const handleCreate = useCallback(
        async (values) => {
            try {
                const newProduct = await createProduct(
                    values.image,
                    values.description,
                    values.price
                );
                setMerchandiseItems((prevItems) => [...prevItems, newProduct]);
                setIsCreateModalVisible(false);
                createForm.resetFields();
            } catch (err) {
                setError(err.message);
            }
        },
        [createForm]
    );

    const handleDelete = useCallback((id) => {
        confirm({
            title: 'Are you sure you want to delete this product?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone.',
            onOk: async () => {
                try {
                    await deleteProduct(id);
                    setMerchandiseItems((prevItems) =>
                        prevItems.filter((item) => item._id !== id)
                    );
                    message.success('Product deleted successfully');
                } catch (err) {
                    setError(err.message);
                    message.error('Failed to delete the product');
                }
            },
            onCancel() {
            },
        });
    }, []);

    const handleCancel = useCallback(() => {
        setIsModalVisible(false);
        setIsCreateModalVisible(false);
    }, []);

    useEffect(() => {
        if (currentProduct) {
            form.setFieldsValue({
                image: currentProduct.image,
                description: currentProduct.description,
                price: currentProduct.price,
            });
        }
    }, [currentProduct, form]);

    const handleAddToInvoice = useCallback(
        async (item) => {
            try {
                await addMerchToInvoice(invoice._id, item._id);
                message.success(`${item.description} added to invoice!`);
            } catch (error) {
                message.error('Failed to add item to invoice');
            }
        },
        [invoice._id]
    );

    if (loading) {
        return <Skeleton active />;
    }

    if (error) {
        return (
            <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
            />
        );
    }

    return (
        <div className="merchandise-container">
            <Title level={4} className="title">
                Merchandise
            </Title>
            <Row gutter={[20, 20]} className="cards-container">
                {merchandiseItems.map((item) => (
                    <MerchandiseItem
                        key={item._id}
                        item={item}
                        role={auth.user.role}
                        handleDelete={handleDelete}
                        handleAddToInvoice={handleAddToInvoice}
                        showModal={showModal}
                    />
                ))}
                {auth.user.role === 'admin' && (
                    <Col>
                        <Card
                            bordered={false}
                            className="main-content add-new-product-card"
                            onClick={showCreateModal}
                        >
                            <div className="content">
                                <PlusOutlined
                                    style={{ fontSize: '48px', color: '#8c8c8c' }}
                                />
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>
            {currentProduct && (
                <Modal
                    title="Edit Product"
                    visible={isModalVisible}
                    onCancel={handleCancel}
                    footer={null}
                >
                    <Form form={form} onFinish={handleOk}>
                        <Form.Item name="image" label="Image">
                            <Input />
                        </Form.Item>
                        <Form.Item name="description" label="Description">
                            <Input />
                        </Form.Item>
                        <Form.Item name="price" label="Price">
                            <Input />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Save
                            </Button>
                        </Form.Item>
                    </Form>
                </Modal>
            )}
            <Modal
                title="Create New Product"
                visible={isCreateModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form form={createForm} onFinish={handleCreate}>
                    <Form.Item name="image" label="Image">
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input />
                    </Form.Item>
                    <Form.Item name="price" label="Price">
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Create
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Merchandise;  
